import React, { useState, useEffect } from 'react';
import { IconNews, IconSearch } from '@tabler/icons-react';
import { IconBrandYoutubeFilled } from '@tabler/icons-react';
// import { IconMenu2 } from '@tabler/icons-react';
import { IconBrandTwitterFilled } from '@tabler/icons-react';
import { IconShoppingCart } from '@tabler/icons-react';
import { IconBrandStackoverflow } from '@tabler/icons-react';
import { IconSubtask } from '@tabler/icons-react';
import { IconBrandReddit } from '@tabler/icons-react';
import { IconBrandWikipedia } from '@tabler/icons-react';
import { IconLoader2 } from '@tabler/icons-react';
import { IconShare2 } from '@tabler/icons-react';
import { IconCheck } from '@tabler/icons-react';
import './index.css';

const ResultIcon = ({ type, className = "text-gray-400" }) => {
  switch (type) {
    case 'youtube':
      return <IconBrandYoutubeFilled size={16} className='text-red-500' />;
    case 'news':
      return <IconNews size={16} className={className} />;
    case 'twitter':
      return <IconBrandTwitterFilled size={16} className='text-twitter' />;
    case 'shop':
      return <IconShoppingCart size={16} className={className} />;
    case 'stackoverflow':
      return <IconBrandStackoverflow size={16} className={className} />;
    case 'blog':
      return <IconSubtask size={16} className={className} />;
    case 'reddit':
      return <IconBrandReddit size={16} className='text-orange-500' />;
    case 'wikipedia':
      return <IconBrandWikipedia size={16} className={className} />;
    default:
      return null;
  }
};

const formatUrl = (url) => {
  // Truncate long URLs
  return url.length > 60 ? url.substring(0, 60) + '...' : url;
};

const LoadingSpinner = () => (
  <div className="flex justify-center items-center py-24 z-50">
    <IconLoader2 
      className="animate-spin text-blue-500" 
      size={48}
    />
  </div>
);

const FakeGoogleSearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [theme, setTheme] = useState('light');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchId, setSearchId] = useState(null);
  const [resultCount] = useState(Math.floor(Math.random() * 900000 + 100000));

  const WORKER_URL = 'https://generate-search.henry-jamison4.workers.dev/';

  useEffect(() => {
    const path = window.location.pathname;
    const urlSearchId = path.split('/').pop();
    
    if (urlSearchId && urlSearchId.length > 5) {
      fetchSharedSearch(urlSearchId);
    }
  }, []);

  const fetchSharedSearch = async (id) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${WORKER_URL}/${id}`);
      if (!response.ok) throw new Error('Search not found');
      
      const data = await response.json();
      setQuery(data.query);
      setResults(data.results);
      setHasSearched(true);
      setSearchId(data.searchId);
    } catch (err) {
      setError('Could not load shared search');
    } finally {
      setIsLoading(false);
    }
  };


  const toggleTheme = () => {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  
  // Toggle this for local development without API
  const USE_MOCK_DATA = false;
  // setIsLoading(true);
  // const generateMockResults = (searchQuery) => {
  //   // Our existing mock data generation logic
  //   const resultTypes = ['news', 'reddit', 'youtube', 'wikipedia'];
  //   return resultTypes
  //     .sort(() => Math.random() - 0.5)
  //     .map(type => generateFakeResult(type, searchQuery));
  // };

  const fetchApiResults = async (searchQuery) => {
  try {
    const response = await fetch(WORKER_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ query: searchQuery })
    });

    if (!response.ok) {
      if (response.status === 429) {
        throw new Error('Rate limit exceeded. Please try again later.');
      }
      throw new Error('Failed to fetch results');
    }

    const data = await response.json();
    if (data.searchId) {
      setSearchId(data.searchId);
      // Update URL without reload
      window.history.pushState({}, '', `/${data.searchId}`);
    }
    return data.results;
  } catch (err) {
    console.error('API Error:', err);
    throw err;
  }
};
const ShareButton = ({ searchId }) => {
  const [copied, setCopied] = useState(false);
  
  const shareUrl = `${window.location.origin}/${searchId}`;
  
  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (!searchId) return null;

  return (
    <button 
      onClick={handleShare} 
      className="flex items-center gap-2 text-sm text-blue-500 hover:text-blue-600"
    >
       {copied ? <IconCheck size={20} /> : <IconShare2 size={20} />}
    </button>
  );
};

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    setIsLoading(true);
    setError(null);
    setResults([]); // Clear previous results while loading

    try {
      let searchResults;
      if (USE_MOCK_DATA) {
        // Use mock data for development
        // searchResults = generateMockResults(query);
      } else {
        // Use real API data
        searchResults = await fetchApiResults(query);
      }

      setResults(searchResults);
      setHasSearched(true);
    } catch (err) {
      setError('Failed to generate results. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`${theme === 'light' ? 'bg-white text-black' : 'bg-[#1f1f1f] text-gray-100'} min-h-screen w-auto sm:mx-auto`}>
      {/* Header */}
      <div className={`${hasSearched ? 'border-b' : 'w-full'}`}>
        <div className={`${hasSearched ? 'px-4 md:px-6 lg:mx-32 py-4' : 'w-full'}`}>
          {!hasSearched ? (
            // Centered layout for initial state
            <div className="relative w-full">
              {/* Dark mode toggle - absolute positioned in top right */}
              <div className="absolute top-2 right-4 px-4">
                <button 
                  onClick={toggleTheme} 
                  className="text-blue-500 text-sm border px-2 py-1 rounded-full"
                >
                  {theme === 'light' ? '🌙' : '☀️'}
                </button>
              </div>

              {/* Main content - show either spinner or search */}
              <div className="flex flex-col items-center justify-start min-h-[80vh] w-full pt-32">
                {/* {isLoading ? (
                  <LoadingSpinner />
                ) : ( */}
                  <>
                    <svg 
                      height="70" 
                      viewBox="0 0 92 30"
                      width="272"
                      xmlns="http://www.w3.org/2000/svg"
                      className={`${theme === 'light' ? 'fill-[#5F6368]' : 'fill-white'} mb-8`}
                    >
                      <path d="M38.9 15.51c0 4.26-3.32 7.39-7.4 7.39s-7.4-3.14-7.4-7.39c0-4.28 3.32-7.39 7.4-7.39s7.4 3.1 7.4 7.39zm-3.24 0c0-2.66-1.93-4.48-4.16-4.48-2.23 0-4.16 1.82-4.16 4.48 0 2.63 1.93 4.48 4.16 4.48 2.23 0 4.16-1.85 4.16-4.48zm-23.7 7.47C5.63 22.98.31 17.83.31 11.5S5.63.02 11.96.02c3.5 0 5.99 1.37 7.87 3.16L17.62 5.4c-1.34-1.26-3.16-2.24-5.66-2.24-4.62 0-8.23 3.72-8.23 8.34 0 4.62 3.61 8.34 8.23 8.34 3 0 4.7-1.2 5.79-2.3.9-.9 1.49-2.2 1.74-4.17H12v-3.14h10.52c.11.56.17 1.23.17 1.96 0 2.35-.64 5.49-2.72 7.56-2.02 2.11-4.59 3.23-8.01 3.23zm42.94-7.47c0 4.26-3.32 7.39-7.4 7.39s-7.4-3.14-7.4-7.39c0-4.28 3.32-7.39 7.4-7.39s7.4 3.1 7.4 7.39zm-3.24 0c0-2.66-1.93-4.48-4.16-4.48-2.23 0-4.16 1.82-4.16 4.48 0 2.63 1.93 4.48 4.16 4.48 2.23 0 4.16-1.85 4.16-4.48zM70 8.56v13.27c0 5.46-3.05 7.7-6.86 7.7-3.58 0-5.74-2.41-6.55-4.37l2.83-1.18c.5 1.2 1.74 2.63 3.72 2.63 2.44 0 3.78-1.51 3.78-4.34v-1.06h-.11c-.73.9-2.04 1.68-3.81 1.68-3.7 0-7-3.22-7-7.36 0-4.17 3.3-7.42 7-7.42 1.76 0 3.08.78 3.81 1.65h.11v-1.2H70zm-2.86 6.97c0-2.6-1.74-4.51-3.95-4.51-2.24 0-3.95 1.9-3.95 4.51 0 2.58 1.71 4.45 3.95 4.45 2.22.01 3.95-1.87 3.95-4.45zM75 1.17V22.9h-3V1.17h3zm12.5 16.77l2.48 1.68c-.8 1.2-2.73 3.28-6.06 3.28-4.13 0-7.22-3.25-7.22-7.39 0-4.4 3.11-7.39 6.86-7.39 3.78 0 5.62 3.05 6.23 4.7l.31.85-9.71 4.08c.74 1.48 1.9 2.24 3.53 2.24s2.76-.82 3.58-2.05zm-7.63-2.66l6.5-2.74c-.36-.92-1.43-1.57-2.7-1.57-1.62 0-3.88 1.46-3.8 4.31z">
                      </path>
                    </svg>
                    <div className="w-auto sm:w-full max-w-[584px] px-4 sm:px-6">
                      <form onSubmit={handleSearch} className="relative w-full flex items-center">
                        <input
                          type="text"
                          value={query}
                          onChange={(e) => setQuery(e.target.value)}
                          placeholder="Search anything..."
                          className={`w-full min-h-[45px] p-3 pr-12 border rounded-full shadow-sm hover:shadow-md focus:outline-none truncate
                            ${theme === 'dark' 
                              ? 'bg-[#4d5156] border-gray-700 text-gray-100 hover:bg-[#5f6368]' 
                              : ''
                            }`}
                        />
                        <button
                          type="submit"
                          className={`absolute right-6 top-1/2 -translate-y-1/2 text-[#8ab4f8]`}
                        >
                          <IconSearch size={20} />
                        </button>
                      </form>
                    </div>
                  </>
                  {isLoading && <LoadingSpinner />}
              </div>
            </div>
          ) : (
            // Original header layout for search results
            <div className="flex justify-between items-center">
              {/* Left side: Logo and Search */}
              <div className="flex items-center gap-2 md:gap-8 w-full max-w-[800px]">
                <svg 
                  height="24"
                  viewBox="0 0 92 30"
                  width="74"
                  xmlns="http://www.w3.org/2000/svg"
                  className={`${theme === 'light' ? 'fill-[#5F6368]' : 'fill-white'} hidden sm:block flex-shrink-0`}
                >
                  <path d="M38.9 15.51c0 4.26-3.32 7.39-7.4 7.39s-7.4-3.14-7.4-7.39c0-4.28 3.32-7.39 7.4-7.39s7.4 3.1 7.4 7.39zm-3.24 0c0-2.66-1.93-4.48-4.16-4.48-2.23 0-4.16 1.82-4.16 4.48 0 2.63 1.93 4.48 4.16 4.48 2.23 0 4.16-1.85 4.16-4.48zm-23.7 7.47C5.63 22.98.31 17.83.31 11.5S5.63.02 11.96.02c3.5 0 5.99 1.37 7.87 3.16L17.62 5.4c-1.34-1.26-3.16-2.24-5.66-2.24-4.62 0-8.23 3.72-8.23 8.34 0 4.62 3.61 8.34 8.23 8.34 3 0 4.7-1.2 5.79-2.3.9-.9 1.49-2.2 1.74-4.17H12v-3.14h10.52c.11.56.17 1.23.17 1.96 0 2.35-.64 5.49-2.72 7.56-2.02 2.11-4.59 3.23-8.01 3.23zm42.94-7.47c0 4.26-3.32 7.39-7.4 7.39s-7.4-3.14-7.4-7.39c0-4.28 3.32-7.39 7.4-7.39s7.4 3.1 7.4 7.39zm-3.24 0c0-2.66-1.93-4.48-4.16-4.48-2.23 0-4.16 1.82-4.16 4.48 0 2.63 1.93 4.48 4.16 4.48 2.23 0 4.16-1.85 4.16-4.48zM70 8.56v13.27c0 5.46-3.05 7.7-6.86 7.7-3.58 0-5.74-2.41-6.55-4.37l2.83-1.18c.5 1.2 1.74 2.63 3.72 2.63 2.44 0 3.78-1.51 3.78-4.34v-1.06h-.11c-.73.9-2.04 1.68-3.81 1.68-3.7 0-7-3.22-7-7.36 0-4.17 3.3-7.42 7-7.42 1.76 0 3.08.78 3.81 1.65h.11v-1.2H70zm-2.86 6.97c0-2.6-1.74-4.51-3.95-4.51-2.24 0-3.95 1.9-3.95 4.51 0 2.58 1.71 4.45 3.95 4.45 2.22.01 3.95-1.87 3.95-4.45zM75 1.17V22.9h-3V1.17h3zm12.5 16.77l2.48 1.68c-.8 1.2-2.73 3.28-6.06 3.28-4.13 0-7.22-3.25-7.22-7.39 0-4.4 3.11-7.39 6.86-7.39 3.78 0 5.62 3.05 6.23 4.7l.31.85-9.71 4.08c.74 1.48 1.9 2.24 3.53 2.24s2.76-.82 3.58-2.05zm-7.63-2.66l6.5-2.74c-.36-.92-1.43-1.57-2.7-1.57-1.62 0-3.88 1.46-3.8 4.31z">
                  </path>
                </svg>
                <form onSubmit={handleSearch} className="w-full">
                  <div className="relative">
                    <input
                      type="text"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search anything..."
                      className={`w-full min-h-[45px] p-3 pr-14 border rounded-full shadow-sm hover:shadow-md focus:outline-none truncate
                        ${theme === 'dark' 
                          ? 'bg-[#4d5156] border-gray-700 text-gray-100 hover:bg-[#5f6368]' 
                          : ''
                        }`}
                    />
                    <button
                      type="submit"
                      className={`absolute right-4 top-1/2 -translate-y-1/2 text-[#8ab4f8]`}
                    >
                      <IconSearch size={20} />
                    </button>
                  </div>
                </form>
              </div>

              {/* Right side: Theme toggle */}
              <div className="flex items-center gap-4 mx-4 flex-shrink-0">
                {/* <IconMenu2 className="text-gray-600 hidden md:block" size={24} /> */}
                <ShareButton searchId={searchId} />
                <button 
                  onClick={toggleTheme} 
                  className="text-blue-500 text-sm border px-2 py-1 md:px-3 md:py-1 rounded-full"
                >
                  {theme === 'light' ? '🌙' : '☀️'}
                </button>
              </div>
            </div>
          )}

          {/* Search types */}
          {hasSearched && !isLoading && !error && (
            <div className="overflow-x-auto">
              <div className={`flex gap-6 mt-4 text-sm ${theme === 'light' ? 'text-gray-600' : 'text-gray-400'} min-w-max`}>
                <div className="flex items-center gap-1 border-b-4 border-blue-500 pb-2 text-blue-500 cursor-pointer">
                  <span>All</span>
                </div>
                <div className="flex items-center gap-1 pb-2 cursor-pointer">
                  <span>Images</span>
                </div>
                <div className="flex items-center gap-1 pb-2 cursor-pointer">
                  <span>Videos</span>
                </div>
                <div className="flex items-center gap-1 pb-2 cursor-pointer">
                  <span>News</span>
                </div>
                <div className="flex items-center gap-1 pb-2 cursor-pointer">
                  <span>Shopping</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Results */}
      {hasSearched && !error && (
        <div className="w-auto px-4 md:px-6 lg:mx-32 py-4">
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <>
              <div className={`text-sm ${theme === 'light' ? 'text-gray-600' : 'text-gray-400'} mb-4`}>
                About {resultCount.toLocaleString()} results
              </div>
              <div className="space-y-8">
                {results.map((result, index) => (
                  <div key={index} className="max-w-xs sm:max-w-2xl">
                    <div className="flex items-center gap-2 text-sm">
                      <ResultIcon type={result.type} className={theme === 'light' ? 'text-gray-400' : 'text-gray-500'} />
                      <span className={`${theme === 'light' ? 'text-gray-600' : 'text-gray-400'} truncate`}>
                        {formatUrl(result.url)}
                      </span>
                    </div>
                    <a 
                      href={result.url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className={`text-xl ${theme === 'light' ? 'text-blue-800' : 'text-blue-400'} hover:underline cursor-pointer mt-1 block`}
                    >
                      {result.title}
                    </a>
                    <div className={`text-sm ${theme === 'light' ? 'text-gray-800' : 'text-gray-300'} mt-1`}>
                      {result.description}
                      {result.type === 'shop' && result.price && (
                        <span className="ml-2 text-green-600 font-medium">{result.price}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default FakeGoogleSearch;